import { Component, OnDestroy, OnInit } from '@angular/core';
import { AlertMessage } from '@assetaccountant/api/models/alert-message';
import { AlertApi } from '@assetaccountant/api/services/alert.api';
import { AppNavigationService } from '@assetaccountant/core/services/app-navigation.service';

const MESSAGE_CHECK_INITIAL_DELAY = 5 * 1000;
const MESSAGE_CHECK_INTERVAL = 5 * 60 * 1000;
const MESSAGE_DISMISSAL = 'alertMessageDismissals';

interface Dismissal {
  id: string;
  organisationId: number | null;
}

@Component({
  selector: 'app-alert-banner',
  templateUrl: './alert-banner.component.html',
  styleUrls: ['./alert-banner.component.scss'],
  standalone: false,
})
export class AlertBannerComponent implements OnInit, OnDestroy {
  alertMessages: AlertMessage[] = [];
  dismissals: Dismissal[] = [];

  get haveMessages() {
    return this.alertMessages.length > 0;
  }

  get getFilteredMessages() {
    const dismissalIds = this.dismissals.map(d => d.id);
    return this.alertMessages.filter(am => !dismissalIds.includes(am.id));
  }

  // refresh the list of messages if the organisation route changes
  // and/or every 5 minutes.
  subscription = this.appNavigationService.onOrganisationChanged.subscribe(organisation => {
    this.getMessages(organisation.organisationId);
  });

  timer = setInterval(() => {
    const organisationId = this.appNavigationService.getActiveOrganisationId();
    this.getMessages(organisationId);
  }, MESSAGE_CHECK_INTERVAL);

  getMessages(organisationId: number | null) {
    this.alertApi.getAlertMessages({ organisationId }).subscribe(alertsResponse => {
      this.alertMessages = alertsResponse.alertMessages ?? [];
      this.clearObsoleteDismissals();
    });
  }

  addDismissal(id: string, organisationId: number | null) {
    this.dismissals = this.dismissals.concat({ id, organisationId });
    this.saveDismissals();
  }

  getDismissals() {
    const dismissalsJson = localStorage.getItem(MESSAGE_DISMISSAL);
    if (dismissalsJson) {
      this.dismissals = JSON.parse(dismissalsJson);
    }
  }

  clearObsoleteDismissals() {
    if (this.dismissals.length === 0) {
      return;
    }

    const newDismissals: Dismissal[] = [];
    let dismissalsCleaned = false;

    for (const dismissal of this.dismissals) {
      const alertMessage = this.alertMessages.find(
        am => am.id === dismissal.id && am.organisationId === dismissal.organisationId,
      );
      // if a message for the dismissal is not found then it has been deleted or expired centrally and is obsolete.
      if (alertMessage) {
        newDismissals.push(dismissal);
      } else {
        dismissalsCleaned = true;
      }
    }

    if (dismissalsCleaned) {
      this.dismissals = newDismissals;
      this.saveDismissals();
    }
  }

  saveDismissals() {
    const dismissalsJson = JSON.stringify(this.dismissals);
    localStorage.setItem(MESSAGE_DISMISSAL, dismissalsJson);
  }

  dismiss(id: string | null | undefined) {
    const alertMessage = this.alertMessages.find(am => am.id === id);
    if (alertMessage) {
      this.dismissals.push({
        id: alertMessage.id,
        organisationId: alertMessage.organisationId || null,
      });
      this.saveDismissals();
    }
  }

  constructor(
    private appNavigationService: AppNavigationService,
    private alertApi: AlertApi,
  ) {}

  ngOnInit() {
    this.getDismissals();
    setTimeout(() => {
      const organisationId = this.appNavigationService.getActiveOrganisationId();
      this.getMessages(organisationId);
    }, MESSAGE_CHECK_INITIAL_DELAY);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    clearInterval(this.timer);
  }
}
